import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Pencil, Trash2, MapPin } from "lucide-react";
import { hitungMasaKerja } from "@/lib/dates";
import { statusTone, KontrakBadge } from "@/components/workers/workersShared";
import WorkerAvatar from "@/components/WorkerAvatar";

const HEADERS = ["No. Pegawai", "Nama", "Jabatan", "Level", "Lokasi Kerja", "Masa Kerja", "Kontrak",
  "Pendidikan", "Gender", "Pertalife", "Opsi", "Baju", "Celana", "Sepatu", "Status", "Aksi"];

function IsiTabel({ workers, filtered, usiaPensiun, selected, toggleSelect, on }) {
  if (workers === null) {
    return <tr><td colSpan={17} className="px-4 py-12 text-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600 mx-auto" /></td></tr>;
  }
  if (filtered.length === 0) {
    return <tr><td colSpan={17} className="px-4 py-12 text-center text-slate-400" data-testid="workers-empty">Belum ada data pekerja</td></tr>;
  }
  return filtered.map((w) => (
    <tr key={w.id} onClick={() => on.detail(w)}
      className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer" data-testid={`worker-row-${w.id}`}>
      <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={selected.has(w.id)} onCheckedChange={() => toggleSelect(w.id)}
          data-testid={`select-worker-${w.id}`} />
      </td>
      <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">{w.nomor_pegawai}</td>
      <td className="px-4 py-3 text-slate-900 whitespace-nowrap">
        <div className="flex items-center gap-2.5">
          <WorkerAvatar worker={w} size="h-8 w-8" textSize="text-[10px]" />
          {w.nama}
        </div>
      </td>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{w.jabatan}</td>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{w.level_pekerja || "-"}</td>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{w.lokasi_kerja || "-"}</td>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-testid={`masa-kerja-${w.id}`}>{hitungMasaKerja(w.tanggal_masuk)}</td>
      <td className="px-4 py-3 whitespace-nowrap" data-testid={`kontrak-status-${w.id}`}>
        <KontrakBadge worker={w} usiaPensiun={usiaPensiun} />
      </td>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{w.pendidikan || "-"}</td>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{w.gender || "-"}</td>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{w.nomor_pertalife || "-"}</td>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{w.pilihan_opsi || "-"}</td>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{w.ukuran_baju || "-"}</td>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{w.ukuran_celana || "-"}</td>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{w.ukuran_sepatu || "-"}</td>
      <td className="px-4 py-3"><Badge variant="outline" className={`${statusTone(w.status)} font-medium`}>{w.status || "-"}</Badge></td>
      <td className="px-4 py-3">
        <div className="flex gap-1">
          <button onClick={(e) => { e.stopPropagation(); on.map(w); }} data-testid={`map-worker-${w.id}`}
            className="h-8 w-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 transition-colors">
            <MapPin className="h-4 w-4" />
          </button>
          {!on.readOnly && <>
          <button onClick={(e) => { e.stopPropagation(); on.edit(w); }} data-testid={`edit-worker-${w.id}`}
            className="h-8 w-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors">
            <Pencil className="h-4 w-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); on.hapus(w); }} data-testid={`delete-worker-${w.id}`}
            className="h-8 w-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors">
            <Trash2 className="h-4 w-4" />
          </button>
          </>}
        </div>
      </td>
    </tr>
  ));
}

// Tabel data pekerja (desktop)
export default function WorkersTable({
  workers, filtered, usiaPensiun, selected, toggleSelect, allSelected, toggleSelectAll, on,
}) {
  return (
    <Card className="border-slate-200 shadow-none rounded-lg overflow-hidden hidden md:block">
      <div className="overflow-auto max-h-[72vh]" data-testid="workers-table-wrap">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 shadow-[0_1px_0_0_#E2E8F0]">
            <tr className="bg-slate-50 text-left">
              <th className="px-4 py-3 w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
              </th>
              {HEADERS.map((h) => (
                <th key={h} className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="workers-table-body">
            <IsiTabel workers={workers} filtered={filtered} usiaPensiun={usiaPensiun}
              selected={selected} toggleSelect={toggleSelect} on={on} />
          </tbody>
        </table>
      </div>
    </Card>
  );
}
